import {
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  signal,
  untracked,
  ViewChild,
} from "@angular/core";
import { JobStateService } from "../job-state.store";
import { CachedProjectService } from "../../../../shared/services/cached-project.service";
import { rxResource } from "@angular/core/rxjs-interop";
import { catchError, EMPTY, forkJoin, map, of, switchMap, tap } from "rxjs";
import { ImageLoaderService } from "../../../../shared/services/image-loader.service";
import { SliceType } from "../../../../models/sliceType";
import { ViewerSettingsService } from "../viewer-settings.store";
import { BatchScreenshotService } from "../batch-screenshot.service";
import { NzModalService } from "ng-zorro-antd/modal";
import { ModelViewerSettingsService } from "../model-viewer-settings.service";
import { MinMaxDialogComponent } from "../min-max-dialog/min-max-dialog.component";
import { MyViewerComponent } from "../my-viewer/my-viewer.component";
import { HorizonWithData } from "../../../../models/generalizedHorizon";

@Component({
  selector: "app-depth-slice",
  standalone: false,
  templateUrl: "./depth-slice.component.html",
  styleUrl: "./depth-slice.component.less",
})
export class DepthSliceComponent {
  @ViewChild("sliceViewer") viewer!: MyViewerComponent;

  protected modelViewerSettings = inject(ModelViewerSettingsService);
  protected viewerSettings = inject(ViewerSettingsService);
  protected jobState = inject(JobStateService);

  private cachedProjectService = inject(CachedProjectService);
  private cachedImageService = inject(ImageLoaderService);
  private nzModalService = inject(NzModalService);

  protected readonly displayData = signal<any>(null);
  protected limits = computed(() => {
    const settings = this.viewerSettings;
    // Get the absolute boundaries
    const tMin = settings.trueMin()["depth"];
    const tMax = settings.trueMax()["depth"];
    // Get the user-defined overrides (if any - the ones in the modal)
    const current = settings.depthMinMax();
    const auto = settings.auto();
    return {
      min: auto ? tMin : (current?.min ?? tMin),
      max: auto ? tMax : (current?.max ?? tMax),
    };
  });

  sliceNumber = SliceType.depth;

  protected xCrop = computed(() => [
    this.jobState.rangesStart.XL(),
    this.jobState.rangesEnd.XL(),
  ]);
  protected yCrop = computed(() => [
    this.jobState.rangesStart.IL(),
    this.jobState.rangesEnd.IL(),
  ]);
  private effectiveVpRef = computed(() => {
    // Only return the ref if the model type matches
    const model = this.jobState.selectedModel();
    return model === "Total_Vp_Update" ? this.jobState.vpRef() : null;
  });
  private effectiveAGCWindow = computed(() => {
    const model = this.jobState.selectedModel();
    return model === "DenChange AGC" ||
      model === "Density plus DenChange Reflectivity AGC"
      ? this.jobState.AGCWindow()
      : null;
  });

  minMaxComputed = computed(() => {
    const settings = this.viewerSettings;
    const current = settings.depthMinMax();
    const auto = untracked(() => settings.auto());
    return auto ? { min: null, max: null } : current;
  });
  // 3. The Image + Bins Resource
  protected sliceResource = rxResource({
    // 'params' combines Global Store signals + Local Component signals
    params: () => ({
      job: this.jobState.jobData(),
      depth: this.jobState.depth(), // from Store
      iteration: this.jobState.iteration(), // from Store
      modelType: this.jobState.selectedModel(), // from Store
      cmap: this.jobState.selectedCmap(), // from Store
      min: this.minMaxComputed().min,
      max: this.minMaxComputed().max,
      vpRef: this.effectiveVpRef(),
      window: this.effectiveAGCWindow(),
      shot: this.jobState.selectedShot(),
    }),

    stream: ({ params }) => {
      // Logic gate: only fetch if we have a valid Job ID and Slice number
      if (!params.job) return EMPTY;
      // Fetch both Image (Blob) and Bins (JSON) in parallel
      return this.cachedProjectService
        .getSlice(
          params.job.id,
          params.modelType,
          params.iteration,
          this.sliceNumber, // inline = 1, crossline = 2, depth = 3 [computed to resolve the assumption of x2IsInline]
          params.depth, // slice number
          params.job.modelGrid.is2d,
          params.cmap,
          params.min, // minVal,
          params.max, //maxVal,
          params.shot, // this.shot,
          1,
          params.vpRef, // this.isVpReference ? this.vpIteration : 0, VP Ref for comparison models
          params.window,
          null, // this.timeSlice
        )
        .pipe(
          switchMap((res) => {
            const { sliceUrl, url, colorBarUrl, trueMax, trueMin } = res;
            this.viewerSettings.setTrueLimits("depth", trueMax, trueMin);

            // saving settings in cache for persistence
            let settings = {
              minMax: { min: params.min, max: params.max },
              cmap: params.cmap,
              auto: untracked(() => this.viewerSettings.auto()),
            };
            if (params.modelType == "Total_Vp_Update")
              settings["vpRef"] = params.vpRef;
            this.modelViewerSettings.saveModelSettings(
              params.job.id,
              params.modelType,
              "depth",
              settings,
            );

            var xDim: number, yDim: number;
            if (this.jobState.jobData().x2_is_inline) {
              xDim = this.jobState.jobData().modelGrid.nx1;
              yDim = this.jobState.jobData().modelGrid.nx2;
            } else {
              xDim = this.jobState.jobData().modelGrid.nx2;
              yDim = this.jobState.jobData().modelGrid.nx1;
            }

            return forkJoin({
              sliceImage: this.cachedImageService.getImageBase64(url),
              colorbar: this.cachedImageService.getImageBase64(colorBarUrl),
              sliceArray: this.cachedProjectService.getSliceArray(
                sliceUrl,
                xDim, // the x dimension of the slice
                yDim, // the y dimension of the slice
                trueMin,
                trueMax,
              ),
              trueMax: of(trueMax),
              trueMin: of(trueMin),
            });
          }),
          catchError((err) => {
            throw Error(
              err.error?.detail ||
                err.message ||
                "An unexpected error occurred",
            );
          }),
        );
    },
  });

  // get shot fits for the iteration
  protected fitInfoResource = rxResource({
    params: () => {
      // only fetch (and surface errors) while the shot overlay is enabled;
      // returning undefined keeps the resource idle and clears stale errors
      if (!this.viewerSettings.showShotOverlay()) return undefined;
      return {
        job: this.jobState.jobData(),
        iteration: this.jobState.iteration(),
      };
    },
    stream: ({ params }) =>
      this.cachedProjectService.getShotOverlay(params.job.id, params.iteration),
  });

  protected mergedShots = computed(() => {
    var showOnlyUsedShots = this.viewerSettings.showOnlyUsedShots();
    // resource success check
    if (this.fitInfoResource.error()) {
      console.error("Fit Info failed:", this.fitInfoResource.error());
      return []; // Return an empty state so the UI doesn't break
    }

    // make the fits with the locations for each shot
    // This will re-calculate instantly whenever either resource updates
    const locs = this.jobState.shotLocationsResource.value();
    const fits = this.fitInfoResource.value();
    if (!locs || !fits) return [];

    var result: any[];
    if (showOnlyUsedShots)
      result = fits.reduce((acc, fit) => {
        // check for skipping and add to acc [actual array] if exists
        if ("traces_fit" in fit) {
          acc.push({
            ...fit,
            ...locs[fit.shot_id],
          });
        }
        return acc;
      }, []);
    else
      result = fits.map((fit) => ({
        ...fit,
        ...locs[fit.shot_id],
      }));

    return result;
  });

  private horizonsForFetch = computed(
    () => ({
      keys: this.jobState
        .selectedHorizon()
        .map((h) => ({ id: h.id, type: h.type })),
      sliceNumber: this.jobState.depth(),
      sliceType: SliceType.depth,
    }),
    {
      equal: (a, b) =>
        a.sliceNumber === b.sliceNumber &&
        a.sliceType === b.sliceType &&
        a.keys.length === b.keys.length &&
        a.keys.every(
          (h, i) => h.id === b.keys[i].id && h.type === b.keys[i].type,
        ),
    },
  );

  // 4. Horizon data resources. will be in conjunction with selectedHorizons
  horizonsDataResource = rxResource<
    HorizonWithData[],
    ReturnType<typeof this.horizonsForFetch>
  >({
    params: () => this.horizonsForFetch(),
    stream: ({ params }) => {
      if (params.keys.length == 0) return of([]);

      const { sliceNumber, sliceType, keys } = params;
      const jobId = this.jobState.jobData().id;
      const selectedHorizon = untracked(() => this.jobState.selectedHorizon());
      const requests = keys.map((key, i) => {
        const h = selectedHorizon[i];
        const fetch$ =
          key.type === "Project"
            ? this.cachedProjectService.getProjectHorizonById(
                key.id,
                sliceNumber,
                sliceType,
              )
            : this.cachedProjectService.getHorizon(
                jobId,
                sliceNumber,
                sliceType,
                key.id,
              );
        return fetch$.pipe(map((data) => ({ ...h, data })));
      });
      return forkJoin(requests);
    },
  });

  // Clean up Object URLs to prevent memory leaks
  constructor() {
    // report image loading to the batch screenshot runner so it knows when the
    // viewer has settled after a job/iteration change
    const batchScreenshotService = inject(BatchScreenshotService);
    effect(() => batchScreenshotService.reportSliceLoading("depth", this.sliceResource.isLoading()));
    inject(DestroyRef).onDestroy(() => batchScreenshotService.reportSliceLoading("depth", false));

    effect((onCleanup) => {
      if (this.sliceResource.error()) return;

      const data: { url: string; bins: any } =
        this.sliceResource.value() as any;
      if (data?.url) {
        onCleanup(() => URL.revokeObjectURL(data.url));
      }
    });

    effect(() => {
      if (this.sliceResource.error()) return;

      const val = this.sliceResource.value();
      if (val) {
        this.displayData.set(val);
      }
    });
  }

  openMinMaxDialog() {
    this.nzModalService.create({
      nzContent: MinMaxDialogComponent,
    });
  }

  resetZoom() {
    this.viewer.resetZoom();
  }
}
